import { useEffect } from 'react';

const SITE_URL = 'https://sealcoatpaving.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/sealcoatpaving_enterprise_brand_system/04_social/open_graph/og_image.png`;
const DEFAULT_TWITTER_IMAGE = `${SITE_URL}/sealcoatpaving_enterprise_brand_system/04_social/twitter/twitter_image.png`;
const DEFAULT_IMAGE_ALT = 'SealcoatPaving.com branded asphalt paving and sealcoating preview';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  jsonLd?: Record<string, any> | Array<Record<string, any>>;
  path?: string;
  image?: string;
  imageAlt?: string;
  keywords?: string[];
  type?: string;
  noindex?: boolean;
}

export { SITE_URL };

const SEO_MANAGED_ATTRIBUTE = 'data-seo-managed';
const SEO_KEY_ATTRIBUTE = 'data-seo-key';

function selectManagedNodes<T extends Element>(selector: string, key: string) {
  return Array.from(document.head.querySelectorAll<T>(`${selector}, [${SEO_KEY_ATTRIBUTE}="${key}"]`));
}

export function SEO({ title, description, canonical, jsonLd, path, image, imageAlt, keywords, type, noindex }: SEOProps) {
  const defaultTitle = "SealcoatPaving.com | Find Trusted Paving & Sealcoating Contractors";
  const defaultDesc = "Search paving and sealcoating contractors by ZIP code. Compare local listings, browse by state, and request quotes from pros near you.";
  const pageTitle = title || defaultTitle;
  const pageDesc = description || defaultDesc;
  const pageUrl = canonical || (path ? `${SITE_URL}${path}` : undefined);
  const ogImage = image || DEFAULT_OG_IMAGE;
  const twitterImage = image || DEFAULT_TWITTER_IMAGE;
  const robots = noindex ? 'noindex, nofollow' : 'index, follow';
  const pageType = type || 'website';
  const resolvedImageAlt = imageAlt || DEFAULT_IMAGE_ALT;
  const keywordContent = keywords?.filter(Boolean).join(', ');

  useEffect(() => {
    const ensureTitle = (content: string) => {
      const matches = selectManagedNodes<HTMLTitleElement>('title', 'title');
      const element = matches[0] ?? document.createElement('title');
      element.setAttribute(SEO_MANAGED_ATTRIBUTE, 'true');
      element.setAttribute(SEO_KEY_ATTRIBUTE, 'title');
      element.textContent = content;
      if (!element.parentElement) {
        document.head.prepend(element);
      }
      matches.slice(1).forEach((match) => match.remove());
    };

    const ensureMeta = (key: string, selector: string, attributes: Record<string, string>, content: string) => {
      const matches = selectManagedNodes<HTMLMetaElement>(selector, key);
      const element = matches[0] ?? document.createElement('meta');
      Object.entries(attributes).forEach(([attribute, value]) => element.setAttribute(attribute, value));
      element.setAttribute(SEO_MANAGED_ATTRIBUTE, 'true');
      element.setAttribute(SEO_KEY_ATTRIBUTE, key);
      element.setAttribute('content', content);
      if (!element.parentElement) {
        document.head.appendChild(element);
      }
      matches.slice(1).forEach((match) => match.remove());
    };

    const ensureLink = (key: string, selector: string, attributes: Record<string, string>, href: string) => {
      const matches = selectManagedNodes<HTMLLinkElement>(selector, key);
      const element = matches[0] ?? document.createElement('link');
      Object.entries(attributes).forEach(([attribute, value]) => element.setAttribute(attribute, value));
      element.setAttribute(SEO_MANAGED_ATTRIBUTE, 'true');
      element.setAttribute(SEO_KEY_ATTRIBUTE, key);
      element.setAttribute('href', href);
      if (!element.parentElement) {
        document.head.appendChild(element);
      }
      matches.slice(1).forEach((match) => match.remove());
    };

    const removeManaged = (key: string, selector: string) => {
      selectManagedNodes(selector, key).forEach((element) => element.remove());
    };

    ensureTitle(pageTitle);
    document.title = pageTitle;

    ensureMeta('description', 'meta[name="description"]', { name: 'description' }, pageDesc);
    ensureMeta('robots', 'meta[name="robots"]', { name: 'robots' }, robots);

    if (keywordContent) {
      ensureMeta('keywords', 'meta[name="keywords"]', { name: 'keywords' }, keywordContent);
    } else {
      removeManaged('keywords', 'meta[name="keywords"]');
    }

    if (pageUrl) {
      ensureLink('canonical', 'link[rel="canonical"]', { rel: 'canonical' }, pageUrl);
      ensureMeta('og:url', 'meta[property="og:url"]', { property: 'og:url' }, pageUrl);
    } else {
      removeManaged('canonical', 'link[rel="canonical"]');
      removeManaged('og:url', 'meta[property="og:url"]');
    }

    ensureMeta('og:title', 'meta[property="og:title"]', { property: 'og:title' }, pageTitle);
    ensureMeta('og:description', 'meta[property="og:description"]', { property: 'og:description' }, pageDesc);
    ensureMeta('og:type', 'meta[property="og:type"]', { property: 'og:type' }, pageType);
    ensureMeta('og:site_name', 'meta[property="og:site_name"]', { property: 'og:site_name' }, 'SealcoatPaving.com');
    ensureMeta('og:locale', 'meta[property="og:locale"]', { property: 'og:locale' }, 'en_US');
    ensureMeta('og:image', 'meta[property="og:image"]', { property: 'og:image' }, ogImage);
    ensureMeta('og:image:width', 'meta[property="og:image:width"]', { property: 'og:image:width' }, '1200');
    ensureMeta('og:image:height', 'meta[property="og:image:height"]', { property: 'og:image:height' }, '630');
    ensureMeta('og:image:alt', 'meta[property="og:image:alt"]', { property: 'og:image:alt' }, resolvedImageAlt);

    ensureMeta('twitter:card', 'meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    ensureMeta('twitter:title', 'meta[name="twitter:title"]', { name: 'twitter:title' }, pageTitle);
    ensureMeta('twitter:description', 'meta[name="twitter:description"]', { name: 'twitter:description' }, pageDesc);
    ensureMeta('twitter:image', 'meta[name="twitter:image"]', { name: 'twitter:image' }, twitterImage);
    ensureMeta('twitter:image:alt', 'meta[name="twitter:image:alt"]', { name: 'twitter:image:alt' }, resolvedImageAlt);

    const jsonLdId = 'seo-json-ld';
    const existingJsonLd = document.getElementById(jsonLdId);
    if (jsonLd) {
      const script = existingJsonLd ?? document.createElement('script');
      script.id = jsonLdId;
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute(SEO_MANAGED_ATTRIBUTE, 'true');
      script.setAttribute(SEO_KEY_ATTRIBUTE, 'json-ld');
      script.textContent = JSON.stringify(jsonLd);
      if (!existingJsonLd) {
        document.head.appendChild(script);
      }
    } else if (existingJsonLd) {
      existingJsonLd.remove();
    }
  }, [jsonLd, keywordContent, ogImage, pageDesc, pageTitle, pageType, pageUrl, resolvedImageAlt, robots, twitterImage]);

  return null;
}
